import { Dispatch } from 'redux';
import { getClaims, createClaim, updateClaim, deleteClaim } from '../../services/api';
import { GetClaimsRequest, CreateClaimRequest, UpdateClaimRequest, ClaimData, PaginationData } from '../../services/types';

// Action Types
export const FETCH_CLAIMS_REQUEST = 'FETCH_CLAIMS_REQUEST';
export const FETCH_CLAIMS_SUCCESS = 'FETCH_CLAIMS_SUCCESS';
export const FETCH_CLAIMS_FAILURE = 'FETCH_CLAIMS_FAILURE';

export const CREATE_CLAIM_REQUEST = 'CREATE_CLAIM_REQUEST';
export const CREATE_CLAIM_SUCCESS = 'CREATE_CLAIM_SUCCESS';
export const CREATE_CLAIM_FAILURE = 'CREATE_CLAIM_FAILURE';

export const UPDATE_CLAIM_REQUEST = 'UPDATE_CLAIM_REQUEST';
export const UPDATE_CLAIM_SUCCESS = 'UPDATE_CLAIM_SUCCESS';
export const UPDATE_CLAIM_FAILURE = 'UPDATE_CLAIM_FAILURE';

export const DELETE_CLAIM_REQUEST = 'DELETE_CLAIM_REQUEST';
export const DELETE_CLAIM_SUCCESS = 'DELETE_CLAIM_SUCCESS';
export const DELETE_CLAIM_FAILURE = 'DELETE_CLAIM_FAILURE';

export const SET_CLAIM_SEARCH_TERM = 'SET_CLAIM_SEARCH_TERM';
export const SET_CLAIM_CURRENT_PAGE = 'SET_CLAIM_CURRENT_PAGE';

// Action Interfaces
export interface FetchClaimsRequestAction {
  type: typeof FETCH_CLAIMS_REQUEST;
}

export interface FetchClaimsSuccessAction {
  type: typeof FETCH_CLAIMS_SUCCESS;
  payload: {
    claims: ClaimData[];
    pagination: PaginationData;
  };
}

export interface FetchClaimsFailureAction {
  type: typeof FETCH_CLAIMS_FAILURE;
  payload: string;
}

export interface CreateClaimRequestAction {
  type: typeof CREATE_CLAIM_REQUEST;
}

export interface CreateClaimSuccessAction {
  type: typeof CREATE_CLAIM_SUCCESS;
  payload: ClaimData;
}

export interface CreateClaimFailureAction {
  type: typeof CREATE_CLAIM_FAILURE;
  payload: string;
}

export interface UpdateClaimRequestAction {
  type: typeof UPDATE_CLAIM_REQUEST;
}

export interface UpdateClaimSuccessAction {
  type: typeof UPDATE_CLAIM_SUCCESS;
  payload: ClaimData;
}

export interface UpdateClaimFailureAction {
  type: typeof UPDATE_CLAIM_FAILURE;
  payload: string;
}

export interface DeleteClaimRequestAction {
  type: typeof DELETE_CLAIM_REQUEST;
}

export interface DeleteClaimSuccessAction {
  type: typeof DELETE_CLAIM_SUCCESS;
  payload: string; // claim ID
}

export interface DeleteClaimFailureAction {
  type: typeof DELETE_CLAIM_FAILURE;
  payload: string;
}

export interface SetClaimSearchTermAction {
  type: typeof SET_CLAIM_SEARCH_TERM;
  payload: string;
}

export interface SetClaimCurrentPageAction {
  type: typeof SET_CLAIM_CURRENT_PAGE;
  payload: number;
}

export type ClaimActionTypes =
  | FetchClaimsRequestAction
  | FetchClaimsSuccessAction
  | FetchClaimsFailureAction
  | CreateClaimRequestAction
  | CreateClaimSuccessAction
  | CreateClaimFailureAction
  | UpdateClaimRequestAction
  | UpdateClaimSuccessAction
  | UpdateClaimFailureAction
  | DeleteClaimRequestAction
  | DeleteClaimSuccessAction
  | DeleteClaimFailureAction
  | SetClaimSearchTermAction
  | SetClaimCurrentPageAction;

// Action Creators
export const fetchClaimsRequest = (): FetchClaimsRequestAction => ({
  type: FETCH_CLAIMS_REQUEST,
});

export const fetchClaimsSuccess = (
  claims: ClaimData[],
  pagination: PaginationData
): FetchClaimsSuccessAction => ({
  type: FETCH_CLAIMS_SUCCESS,
  payload: { claims, pagination },
});

export const fetchClaimsFailure = (error: string): FetchClaimsFailureAction => ({
  type: FETCH_CLAIMS_FAILURE,
  payload: error,
});

export const createClaimRequest = (): CreateClaimRequestAction => ({
  type: CREATE_CLAIM_REQUEST,
});

export const createClaimSuccess = (claim: ClaimData): CreateClaimSuccessAction => ({
  type: CREATE_CLAIM_SUCCESS,
  payload: claim,
});

export const createClaimFailure = (error: string): CreateClaimFailureAction => ({
  type: CREATE_CLAIM_FAILURE,
  payload: error,
});

export const updateClaimRequest = (): UpdateClaimRequestAction => ({
  type: UPDATE_CLAIM_REQUEST,
});

export const updateClaimSuccess = (claim: ClaimData): UpdateClaimSuccessAction => ({
  type: UPDATE_CLAIM_SUCCESS,
  payload: claim,
});

export const updateClaimFailure = (error: string): UpdateClaimFailureAction => ({
  type: UPDATE_CLAIM_FAILURE,
  payload: error,
});

export const deleteClaimRequest = (): DeleteClaimRequestAction => ({
  type: DELETE_CLAIM_REQUEST,
});

export const deleteClaimSuccess = (claimId: string): DeleteClaimSuccessAction => ({
  type: DELETE_CLAIM_SUCCESS,
  payload: claimId,
});

export const deleteClaimFailure = (error: string): DeleteClaimFailureAction => ({
  type: DELETE_CLAIM_FAILURE,
  payload: error,
});

export const setClaimSearchTerm = (searchTerm: string): SetClaimSearchTermAction => ({
  type: SET_CLAIM_SEARCH_TERM,
  payload: searchTerm,
});

export const setClaimCurrentPage = (page: number): SetClaimCurrentPageAction => ({
  type: SET_CLAIM_CURRENT_PAGE,
  payload: page,
});

// Thunk Actions
export const fetchClaims = (params?: GetClaimsRequest) => {
  return async (dispatch: Dispatch<ClaimActionTypes>) => {
    dispatch(fetchClaimsRequest());
    try {
      const response = await getClaims(params);
      if (response.success) {
        dispatch(fetchClaimsSuccess(response.data, response.pagination));
      } else {
        dispatch(fetchClaimsFailure(response.message || 'Failed to fetch claims'));
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch claims';
      dispatch(fetchClaimsFailure(errorMessage));
    }
  };
};

export const addClaim = (data: CreateClaimRequest) => {
  return async (dispatch: Dispatch<ClaimActionTypes>) => {
    dispatch(createClaimRequest());
    try {
      const response = await createClaim(data);
      if (response.success) {
        dispatch(createClaimSuccess(response.data));
        return response;
      } else {
        dispatch(createClaimFailure(response.message || 'Failed to create claim'));
        throw new Error(response.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to create claim';
      dispatch(createClaimFailure(errorMessage));
      throw error;
    }
  };
};

export const editClaim = (claimId: string, data: UpdateClaimRequest) => {
  return async (dispatch: Dispatch<ClaimActionTypes>) => {
    dispatch(updateClaimRequest());
    try {
      const response = await updateClaim(claimId, data);
      if (response.success) {
        dispatch(updateClaimSuccess(response.data));
        return response;
      } else {
        dispatch(updateClaimFailure(response.message || 'Failed to update claim'));
        throw new Error(response.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update claim';
      dispatch(updateClaimFailure(errorMessage));
      throw error;
    }
  };
};

export const removeClaim = (claimId: string) => {
  return async (dispatch: Dispatch<ClaimActionTypes>) => {
    dispatch(deleteClaimRequest());
    try {
      const response = await deleteClaim(claimId);
      if (response.success) {
        dispatch(deleteClaimSuccess(claimId));
        return response;
      } else {
        dispatch(deleteClaimFailure(response.message || 'Failed to delete claim'));
        throw new Error(response.message);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete claim';
      dispatch(deleteClaimFailure(errorMessage));
      throw error;
    }
  };
};

